import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, concatMap, filter, map, mergeMap } from 'rxjs/operators';
import { Observable, EMPTY, switchMap, of, defer, from } from 'rxjs';
import { DbStatus, Recording, RecordingActionSuccessTypeEnum } from '@sleipai/sleip-models';
import * as RecordingActions from '../actions/recording.actions';
import { RecordingService } from '../services/recording.service';

@Injectable()
export class RecordingEffects {
  private recordingService = inject(RecordingService);

  loadRecordings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadRecordings),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      concatMap(() => EMPTY as Observable<{ type: string }>)
    );
  });

  loadSingleRecording$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadSingleRecording),
      map((action) => action.id),
      mergeMap((id) =>
        this.recordingService.getRecordingById(id).pipe(
          filter((data: Recording) => data.dbStatus !== DbStatus.REMOVED),
          map((data: Recording) =>
            RecordingActions.addSingleRecording({ recording: data })
          ),
          catchError(() => {
            return EMPTY;
          })
        )
      )
    );
  });

  loadRecordingsByIds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadRecordingsByIds),
      mergeMap((action) => {
        return this.recordingService
          .getRecordingsByIds(action.recordingIds)
          .pipe(
            map((recordings) => {
              return RecordingActions.addRecordings({
                recordings,
                hasMore: false,
              });
            })
          );
      })
    );
  });

  loadRecordingsByType$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadRecordingListByType),
      map((action) => action),
      mergeMap(({ status, dbStatus, offset }) => {
        return this.recordingService
          .getPagedRecording(status, dbStatus, offset)
          .pipe(
            map((recordings) => {
              return RecordingActions.loadRecordingListByTypeSuccess({
                recordings: recordings,
              });
            }),
            catchError(() => EMPTY)
          );
      })
    );
  });

  loadRecordingsCountInFirestoreDb$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadRecordingsCountInFirestoreDb),
      map((action) => action),
      mergeMap(({ horseId, filterMap }) => {
        return this.recordingService
          .getRecordingsCountInFirestoreDb(horseId, filterMap)
          .pipe(
            map((data) => {
              return RecordingActions.loadRecordingsCountInFirestoreDbSuccess({
                count: data.data().count,
              });
            }),
            catchError((error) => {
              console.log(error);
              return [RecordingActions.loadRecordingsFailure({ error })];
            })
          );
      })
    );
  });

  getRecordingsListInFirestoreDb$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadRecordingsListInFirestoreDb),
      switchMap(({ queryConfig, horseId }) => {
        return this.recordingService
          .getRecordingsListInFirestoreDb(queryConfig, horseId)
          .pipe(
            map((data) => {
              return RecordingActions.loadRecordingsListInFirestoreDbSuccess({
                data,
              });
            }),
            catchError((error) => {
              console.log(error);
              return [RecordingActions.loadRecordingsFailure({ error })];
            })
          );
      })
    );
  });

  softDeleteRecording$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.removeRecording),
      mergeMap(({ recordingId }) =>
        defer(() => this.recordingService.removeRecording(recordingId)).pipe(
          switchMap(() =>
            from([
              RecordingActions.removeRecordingSuccess({ recordingId }),
              RecordingActions.resetRemoveRecordingFlag(),
            ])
          ),
          catchError((error) =>
            of(RecordingActions.removeRecordingFailure({ error }))
          )
        )
      )
    );
  });

  // Infinite recording list pagination start
  loadRecordingsInfiniteScrollListPageForHorse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.loadRecordingsInfiniteScrollListPageForHorse),
      switchMap(({ horseId, queryConfig }) => {
        return this.recordingService
          .fetchRecordingsInfiniteScrollListPageForHorse(horseId, queryConfig)
          .pipe(
            map((recordingList) => {
              return RecordingActions.loadRecordingsInfiniteScrollListPageForHorseSuccess(
                {
                  recordingList,
                }
              );
            }),
            catchError((error) => {
              console.error(error);
              return [RecordingActions.loadRecordingsFailure({ error })];
            })
          );
      })
    );
  });

  //// Dashboard 2.0 recordings list pagination start ////

  loadRecordingsListForInfiniteScrollInDashBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.loadRecordingsListForInfiniteScrollInDashBoard),
      mergeMap(({ reset = false, queryConfig }) => {
        return this.recordingService
          .fetchRecordingsListForInfiniteScrollInDashBoard(reset, queryConfig)
          .pipe(
            map((result) => {
              return RecordingActions.loadRecordingsListForInfiniteScrollInDashBoardSuccess(
                {
                  recordings: result.recordings,
                  hasMore: result.hasMore,
                }
              );
            }),
            catchError((error) => {
              console.error(error);
              return of(
                RecordingActions.loadRecordingsFailure({
                  error:
                    error.message ||
                    'An error occurred while loading recordings',
                })
              );
            })
          );
      })
    )
  );

  loadRecordingsCountForInfiniteScrollInDashBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.loadRecordingsCountForInfiniteScrollInDashBoard),
      switchMap(({ reset = false, queryConfig }) => {
        return this.recordingService
          .fetchRecordingsCountForInfiniteScrollInDashBoard(reset, queryConfig)
          .pipe(
            map((recordingsCount) => {
              return RecordingActions.loadRecordingsCountForInfiniteScrollInDashBoardSuccess(
                {
                  recordingsCount,
                }
              );
            }),
            catchError((error) => {
              console.log(error);
              return of(
                RecordingActions.loadRecordingsFailure({
                  error:
                    error.message ||
                    'An error occurred while loading recordings count',
                })
              );
            })
          );
      })
    )
  );

  retryLoadRecordingsForInfiniteScrollInDashBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.retryLoadRecordingsForInfiniteScrollInDashBoard),
      map(({ queryConfig }) =>
        RecordingActions.loadRecordingsListForInfiniteScrollInDashBoard({
          reset: true,
          queryConfig,
        })
      )
    )
  );
  //// Dashboard 2.0 recordings list pagination end ////

  // Infinite recording list pagination end

  updatedRecordingData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RecordingActions.updateRecordingData),
      switchMap(({ updatedRecordingData }) =>
        defer(() =>
          this.recordingService.updateRecordingData(updatedRecordingData)
        ).pipe(
          switchMap(() => {
            return [
              RecordingActions.updateRecordingDataSuccess(),
              RecordingActions.resetRecordingUpdatedFlag(),
            ];
          }),
          catchError((error) => {
            console.log(error);
            return of(RecordingActions.updateRecordingDataFailure({ error }));
          })
        )
      )
    );
  });

  loadEligibleRecordingsToLinkForARecording$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.loadEligibleRecordingsToLinkForARecording),
      mergeMap(({ horseId, recordingId, creationDate, longeingDirection }) => {
        return this.recordingService
          .fetchEligibleRecordingsToLinkForARecording(
            horseId,
            recordingId,
            creationDate,
            longeingDirection
          )
          .pipe(
            map((recordings) => {
              return RecordingActions.loadEligibleRecordingsToLinkForCombinedViewForRecordingSuccess(
                {
                  recordings,
                }
              );
            }),
            catchError((error) => {
              console.error(error);
              return of(
                RecordingActions.recordingLinkingForCombinedViewRelatedOperationFailure(
                  {
                    error:
                      error.message ||
                      'An error occurred while loading eligible recordings for linking',
                  }
                )
              );
            })
          );
      })
    )
  );

  updateCombinedViewLinkedRecording$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.updateCombinedViewLinkedRecording),
      mergeMap(
        ({
          selectedRecordingId,
          newLinkedRecordingId,
          previousLinkedRecordingId,
        }) => {
          return defer(() =>
            this.recordingService.updateCombinedViewLinkedRecording(
              selectedRecordingId,
              newLinkedRecordingId,
              previousLinkedRecordingId
            )
          ).pipe(
            switchMap(() => {
              return [RecordingActions.resetRecordingUpdatedFlag()];
            }),
            catchError((error) => {
              console.error(error);
              return [
                RecordingActions.recordingLinkingForCombinedViewRelatedOperationFailure(
                  {
                    error:
                      error.message ||
                      'An error occurred while updating linked recording for combined view',
                  }
                ),
                RecordingActions.resetRecordingUpdatedFlag(),
              ];
            })
          );
        }
      )
    )
  );

  batchRemoveRecordings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.batchRemoveRecordings),
      switchMap(({ recordingIds }) =>
        defer(() => this.recordingService.batchRemoveRecordings(recordingIds)).pipe(
          switchMap((response) => {
            const failedIds = response?.failedIds ?? [];
            const removedIds = recordingIds.filter((id) => !failedIds.includes(id));
            return [
              RecordingActions.batchRemoveRecordingsSuccess({
                removedIds,
                failedIds,
                recordingActionSuccessType: RecordingActionSuccessTypeEnum.RECORDING_BATCH_REMOVE,
              }),
            ];
          }),
          catchError((error) =>
            of(
              RecordingActions.batchRemoveRecordingsFailure({
                error: { ...error, message: 'An error occurred while removing recordings' },
              })
            )
          )
        )
      )
    )
  );

  batchMoveRecordingsToHorse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.batchMoveRecordingsToHorse),
      switchMap(({ recordingIds, horseId }) =>
        defer(() => this.recordingService.batchMoveRecordingsToHorse(recordingIds, horseId)).pipe(
          switchMap((response) => {
            const failedIds = response?.failedIds ?? [];
            const movedIds = recordingIds.filter((id) => !failedIds.includes(id));
            return [
              RecordingActions.batchMoveRecordingsToHorseSuccess({
                movedIds,
                failedIds,
                recordingActionSuccessType: RecordingActionSuccessTypeEnum.RECORDING_BATCH_MOVE,
              }),
            ];
          }),
          catchError((error) =>
            of(
              RecordingActions.batchMoveRecordingsToHorseFailure({
                error: { ...error, message: 'An error occurred while moving recordings' },
              })
            )
          )
        )
      )
    )
  );

  loadCombinedLongeingsForHorse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecordingActions.loadCombinedLongeingsForHorse),
      switchMap(({ horseId }) =>
        this.recordingService.fetchCombinedLongeingsByHorseId(horseId).pipe(
          map((combinedLongeings) =>
            RecordingActions.setCombinedLongeingsForHorse({ combinedLongeings })
          ),
          catchError((error) => {
            console.error('Error loading combined longeings for horse:', error);
            return of(
              RecordingActions.loadCombinedLongeingsForHorseFailure({ error })
            );
          })
        )
      )
    )
  );

  constructor(private actions$: Actions) { }
}
